import React from "react";
import {connect} from "react-redux";
import {DateTime} from "luxon";
import NetworkAwareBusyOverlayCmp from "../NetworkAwareBusyOverlayCmp";
import {ReduxActiveWebcastSubStateType} from "@nextlot/core/webcast/redux/reducerActiveWebcast";
import {ChatRoom, Participant} from "@nextlot/core/webcast/data/data_types_definitions";
import {TaggedLogger} from "@nextlot/core/utilities";
import {AuctionsManager} from "@nextlot/core/webcast";
import {ReduxWebcastChatType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastChat";
import {ChannelEnum} from "@nextlot/core/webcast/redux/actions";


type RootCmpPropsType = {
    chat: ReduxWebcastChatType,
    participantsByUserId: Map<number, Participant>,

    openChatRoom: (roomId:number)=>null|(()=>void),
    closeChatRoom: (roomId:number)=>void,
};

type RootCmpStateType = {
    chatMessageText: string,
}


const _logger = TaggedLogger.get('ClerkChatTabContentCmp');


class ClerkChatTabContentCmp extends React.Component<RootCmpPropsType, RootCmpStateType> {
    private readonly refMessagesListBottom: any;
    private readonly refInputChatTextbox: any;


    constructor(props) {
        super(props);
        _logger.debug('.constructor: ', props);

        this.refMessagesListBottom = React.createRef();
        this.refInputChatTextbox = React.createRef();


        this.state = {
            chatMessageText: '',
        }
    }


    componentDidMount(): void {
        this.scrollMessagesListToBottom();
    }



    componentDidUpdate(prevProps:RootCmpPropsType): void {
        const currentActiveChatRoomId:number = this.props.chat.activeChatRoomId;
        const currentActiveChatRoom:ChatRoom = this.props.chat.chatRoomsById.get(currentActiveChatRoomId);

        const prevActiveChatRoomId:number = prevProps.chat.activeChatRoomId;
        const prevActiveChatRoom:ChatRoom = prevProps.chat.chatRoomsById.get(prevActiveChatRoomId);

        if (currentActiveChatRoom) {
            if (currentActiveChatRoomId !== prevActiveChatRoomId) {
                // active selected ChatRoom changed => focus input
                this.refInputChatTextbox && this.refInputChatTextbox.current && this.refInputChatTextbox.current.focus();
                this.scrollMessagesListToBottom();
            }
            else {
                // same ChatRoom.id, but different objects? (means chat messages changed)
                if (prevActiveChatRoom !== currentActiveChatRoom) {
                    this.scrollMessagesListToBottom();
                }
            }
        }
    }





    getChatRoomLabel(chatRoom:ChatRoom) {
        let chatRoomLabel;
        if (chatRoom.id === -1) {
            chatRoomLabel = 'ALL (BROADCAST)';
        }
        else if (chatRoom.id === 0) {
            chatRoomLabel = 'ADMIN';
        }
        else {
            const roomOwner:Participant = this.props.participantsByUserId.get(chatRoom.id);
            if (!roomOwner) {
                return '[offline] Participant#' + chatRoom.id;
            }
            chatRoomLabel = `${roomOwner.bidderNo} (${roomOwner.bidderLabel})`;
        }
        return chatRoomLabel;
    }



    scrollMessagesListToBottom = () => {
        this.refMessagesListBottom && this.refMessagesListBottom.current && this.refMessagesListBottom.current.scrollIntoView({ behavior: 'auto' });
    };



    handleClick_ChatRoomListItem = (chatRoom:ChatRoom) => (event) => {
        this.setState({
            chatMessageText: ''
        });

        this.props.openChatRoom(chatRoom.id);
    };


    handleClick_ChatRoomClose = (chatRoom:ChatRoom) => (event) => {
        this.setState({
            chatMessageText: ''
        });

        this.props.closeChatRoom(chatRoom.id);
    };


    handleChange_ChatMessageInput = (event) => {
        this.setState({ chatMessageText: event.target.value });
    };


    handleFormSubmit_ChatMessageForm = (event) => {
        event.preventDefault();
        AuctionsManager.currentWebcastAuctionHandler.controller.sendChatMessage(this.props.chat.activeChatRoomId, this.state.chatMessageText);
        this.setState({ chatMessageText: '' });
    };





    renderChatRoomsListItem(chatRoom:ChatRoom) {
        if (! this.props.participantsByUserId.get(chatRoom.id)) {

        }

        const chatRoomLabel = this.getChatRoomLabel(chatRoom);
        return (
            <div key={chatRoom.id} className={`chat-rooms-list__item ${chatRoom.id === this.props.chat.activeChatRoomId ? 'active' : null}`}>
                <div className="room-label" onClick={this.handleClick_ChatRoomListItem(chatRoom)}>
                    <span className="participant">{chatRoomLabel}</span>
                    {
                        (chatRoom.unreadCount && chatRoom.unreadCount > 0) ?
                            <span className="unread">({chatRoom.unreadCount})</span>
                            :
                            null
                    }
                </div>

                {(chatRoom.id !== 0 && chatRoom.id !== -1) &&
                <div className="close" onClick={this.handleClick_ChatRoomClose(chatRoom)}>&times;</div>
                }
            </div>
        )
    }




    renderActiveChatRoom() {
        const chatRoom:ChatRoom = this.props.chat.chatRoomsById.get(this.props.chat.activeChatRoomId);

        if (! chatRoom) {
            return null;
        }

        const chatRoomLabel = this.getChatRoomLabel(chatRoom);
        return (
            <div className="chat__active-room-container">
                <div className="active-room__title">Chat: <strong>{chatRoomLabel}</strong></div>

                <div className="active-room__messages-list-container">
                    <div className="messages-list__scrollable-container">
                        {chatRoom.messages.map(chatMsg => this.renderChatMessageListItem(chatMsg))}
                        <div ref={this.refMessagesListBottom} style={{ fontSize: 2 }}>&nbsp;</div>
                    </div>
                </div>

                <form className="active-room__input-text-line" onSubmit={this.handleFormSubmit_ChatMessageForm}>
                    <div className="canned-messages-control">&nbsp;&equiv;&nbsp;</div>
                    <input
                        ref={this.refInputChatTextbox}
                        onChange={this.handleChange_ChatMessageInput}
                        value={this.state.chatMessageText} />
                    <button type="submit" disabled={ ! this.state.chatMessageText}>&rarr;</button>
                </form>
            </div>
        )
    }


    renderChatMessageListItem(chatMsg) {
        const direction = chatMsg.sender === this.props.chat.myUserInfo.senderUserId ? 'ours' : 'theirs';
        const dtStr = DateTime.fromMillis(chatMsg.timestamp).toLocal().toFormat('h:mma');
        return (
            <div key={[chatMsg.timestamp, chatMsg.sender].join('-')} className={`messages-list__list-item ${direction}`}>
                <div className="body-text">{chatMsg.body}</div>
                <div className="time-label">{dtStr}</div>
            </div>
        )
    }




    render() {
        // _logger.debug('.render() props:', this.props);

        return (
            <div className="clerk-chat-tab-container">
                <NetworkAwareBusyOverlayCmp wbsCommChannel={ChannelEnum.chat} />

                <div className="chat__rooms-list-container">
                    <div className="rooms-list-container__scrollable-container">
                        {this.props.chat.chatRooms
                            .filter((chatRoom:ChatRoom) => !chatRoom.isClosed)
                            .map((chatRoom:ChatRoom) => this.renderChatRoomsListItem(chatRoom))}
                    </div>
                </div>

                {this.renderActiveChatRoom()}
            </div>
        )
    }
}



export default connect(({ activeWebcast }: {activeWebcast:ReduxActiveWebcastSubStateType}) => {
    return {
        webcastStatus: activeWebcast.webcastStatus,
        participantsByUserId: activeWebcast.webcastParticipants.mapByUserId,
        chat: activeWebcast.webcastChat,
    };
})(ClerkChatTabContentCmp);


